/**
 * Pano duvarı (#570): ekranı kaplayan, sırayla dönen pano(lar). İki yerde:
 *  - boşta kalınca (AppShell: kabuğun ÜSTÜNE biner, kaldığı sekme altında bekler),
 *  - kiosk açılışı (`?dashboard=…`: kabuk hiç çizilmez).
 *
 * Tam ekran yalnız DENENİR: tarayıcı `requestFullscreen`'i kullanıcı hareketi olmadan
 * reddeder; boşta geçişte reddedilir ama duvar zaten pencereyi kaplar. Kioskta tarayıcı
 * `--kiosk` ile açılır. Duvar sökülünce tam ekrandan çıkılır.
 */
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { DashboardViewer } from '@/features/dashboard/ui/DashboardViewer';
import { useDashboards } from '@/features/shell/navMenu/DashboardsGroup';
import { EmptyState } from '@/shared/ui/EmptyState';
import { nextIndex } from './kioskParams';

export function tryFullscreen(el: Element): void {
  try {
    if (document.fullscreenElement) return;
    const p = el.requestFullscreen?.();
    if (p && typeof (p as Promise<void>).catch === 'function') (p as Promise<void>).catch(() => {});
  } catch {
    /* kullanıcı hareketi yok: tarayıcı reddetti */
  }
}

export function exitFullscreen(): void {
  try {
    if (!document.fullscreenElement) return;
    const p = document.exitFullscreen?.();
    if (p && typeof (p as Promise<void>).catch === 'function') (p as Promise<void>).catch(() => {});
  } catch {
    /* zaten çıkılmış */
  }
}

export function DashboardWall({
  dashboardIds,
  rotateSec,
  attemptFullscreen = false,
  hint,
}: {
  dashboardIds: string[];
  rotateSec: number;
  attemptFullscreen?: boolean;
  /** Alt köşede küçük yazı ("çıkmak için bir tuşa basın"). */
  hint?: string;
}) {
  const { t } = useTranslation();
  const dashboards = useDashboards();
  // Kullanıcıdan alınmış pano listede kalmış olabilir: liste geldiyse olmayanlar atlanır.
  const ids = useMemo(() => {
    if (!dashboards.length) return dashboardIds;
    const known = new Set(dashboards.map((d) => d.dashboardId.toLowerCase()));
    const kept = dashboardIds.filter((id) => known.has(id.toLowerCase()));
    return kept.length ? kept : dashboardIds;
  }, [dashboards, dashboardIds]);
  const [index, setIndex] = useState(0);

  useEffect(() => {
    if (ids.length < 2) return;
    const id = window.setInterval(
      () => setIndex((i) => nextIndex(i, ids.length)),
      Math.max(10, rotateSec) * 1000,
    );
    return () => window.clearInterval(id);
  }, [ids.length, rotateSec]);

  useEffect(() => {
    if (!attemptFullscreen) return;
    tryFullscreen(document.documentElement);
    return exitFullscreen;
  }, [attemptFullscreen]);

  const current = ids[index % Math.max(1, ids.length)] ?? null;
  const meta = dashboards.find((d) => d.dashboardId.toLowerCase() === current?.toLowerCase());
  const name = meta?.dashboard?.dashboardName ?? '';

  return (
    <div
      className="bg-bg fixed inset-0 z-[90] flex flex-col"
      data-dashboard-wall
      role="region"
      aria-label={name || t('shell.dashboards')}
    >
      {current ? (
        <div className="min-h-0 flex-1">
          <DashboardViewer
            key={current}
            dashboardId={current}
            refreshIntervalSec={meta?.dashboard?.refreshIntervalSec ?? 0}
          />
        </div>
      ) : (
        <EmptyState title={t('dashboard.notFound')} />
      )}
      {/* Ad, sıra ve ipucu alt şeritte, küçük: pano alanından yer almasın. */}
      <div className="text-fg-subtle flex shrink-0 items-center gap-3 px-3 py-1 text-[11px]">
        <span className="text-fg-muted truncate font-medium">{name}</span>
        {ids.length > 1 ? (
          <span className="tabular-nums">
            {(index % ids.length) + 1} / {ids.length}
          </span>
        ) : null}
        {hint ? <span className="ml-auto truncate">{hint}</span> : null}
      </div>
    </div>
  );
}
